import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, BookOpen, FileText, Image as ImageIcon, Share2,
  Plus, Loader2, CheckCircle2, AlertCircle, ExternalLink, Sparkles, Edit3,
} from "lucide-react";
import TopNav from "../components/TopNav";
import api from "../lib/api";

/**
 * BookOverview — single-page snapshot of every artefact (front matter,
 * covers, listings, share link) tied to one book. Helps authors confirm
 * KDP submission readiness at a glance.
 *
 * Anchored on the front-matter project id. Related covers/listings are
 * matched server-side by case-insensitive book_title.
 */

const BookOverview = () => {
  const { fmId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fmId) return;
    let mounted = true;
    setLoading(true);
    api.get(`/book/${fmId}`).then((r) => {
      if (mounted) setData(r.data);
    }).catch((e) => {
      if (mounted) setError(e?.response?.data?.detail || "Could not load book overview.");
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [fmId]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center gap-2 justify-center h-[40vh] text-sm text-[#5F5E5A]" data-testid="book-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading book overview…
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="text-center text-sm text-[#5F5E5A]">
          {error || "No data"}
        </div>
      </Layout>
    );
  }

  const fm = data.front_matter;
  const covers = data.covers || [];
  const listings = data.listings || [];

  const firstCover = covers[0];
  const firstShare = covers.find((c) => c.share_token);

  /* Readiness checklist for KDP submission.
     Each step has: label, complete?, cta -> action. */
  const steps = [
    {
      key: "fm",
      label: "Front matter saved",
      complete: true,
      hint: fm.status === "final" ? "ISBN entered — final" : "Draft (ISBN pending)",
      cta: { label: "Edit", to: `/frontmatter/${fm.id}` },
    },
    {
      key: "isbn",
      label: "ISBN added",
      complete: !!(fm.isbn || "").trim(),
      hint: fm.isbn ? fm.isbn : "Add the KDP-issued ISBN after upload",
      cta: { label: "Open", to: `/frontmatter/${fm.id}` },
    },
    {
      key: "cover",
      label: "Cover project created",
      complete: covers.length > 0,
      hint: covers.length ? `${covers.length} cover${covers.length === 1 ? "" : "s"} linked` : "No matching cover yet",
      cta: covers.length
        ? { label: "Open", to: `/cover/${firstCover.id}` }
        : { label: "Create", to: `/cover/new?from=${fm.id}` },
    },
    {
      key: "listing",
      label: "Amazon listing generated",
      complete: listings.length > 0,
      hint: listings.length ? `${listings.length} version${listings.length === 1 ? "" : "s"} saved` : "Pending",
      cta: { label: listings.length ? "Open" : "Generate", to: "/dashboard" },
    },
    {
      key: "share",
      label: "Share preview link",
      complete: !!firstShare,
      hint: firstShare ? "Public link active" : "Optional — promote on socials",
      cta: firstShare
        ? { label: "Open", href: `${window.location.origin}/api/share/${firstShare.share_token}`, external: true }
        : { label: "Open cover", to: covers.length ? `/cover/${firstCover.id}` : `/cover/new?from=${fm.id}` },
    },
  ];

  const readyCount = steps.filter((s) => s.complete).length;
  const isReady = fm.status === "final" && covers.length > 0;

  return (
    <Layout>
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-[#5F5E5A] hover:text-[#2C2C2A] inline-flex items-center gap-1.5 mb-4"
        data-testid="book-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#5F5E5A] mb-1">Book overview</p>
          <h1
            className="text-3xl tracking-tight text-[#2C2C2A]"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            data-testid="book-title"
          >
            {fm.book_title || "Untitled book"}
          </h1>
          {fm.author && (
            <p className="mt-1 text-sm text-[#5F5E5A]">by {fm.author}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="book-readiness-badge"
            className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
              isReady
                ? "bg-[#E8F6F0] border-[#1D9E75]/40 text-[#1D9E75]"
                : "bg-[#FFF4E5] border-[#E4D6BA] text-[#7A5C0E]"
            }`}
          >
            {isReady ? "Ready for KDP" : `${readyCount} of ${steps.length} ready`}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        {/* Checklist */}
        <section data-testid="book-checklist">
          <h2 className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A] font-medium mb-3">
            KDP submission checklist
          </h2>
          <div className="bg-white border border-[#D3D1C7] rounded-[12px] divide-y divide-[#D3D1C7]">
            {steps.map((s) => (
              <div key={s.key} className="flex items-start gap-3 px-5 py-4" data-testid={`book-step-${s.key}`}>
                {s.complete ? (
                  <CheckCircle2 className="w-5 h-5 text-[#1D9E75] mt-0.5 shrink-0" strokeWidth={1.6} />
                ) : (
                  <AlertCircle className="w-5 h-5 text-[#BA7517] mt-0.5 shrink-0" strokeWidth={1.6} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#2C2C2A]">{s.label}</div>
                  <div className="text-[11px] text-[#5F5E5A] mt-0.5">{s.hint}</div>
                </div>
                {s.cta && (
                  s.cta.external ? (
                    <a
                      href={s.cta.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[#534AB7] hover:underline inline-flex items-center gap-1"
                    >
                      {s.cta.label} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link
                      to={s.cta.to}
                      className="text-xs text-[#534AB7] hover:underline"
                    >
                      {s.cta.label} →
                    </Link>
                  )
                )}
              </div>
            ))}
          </div>

          {/* Linked artefact sections */}
          <h2 className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A] font-medium mt-8 mb-3">
            Linked covers ({covers.length})
          </h2>
          {covers.length === 0 ? (
            <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[10px] p-6 text-center">
              <ImageIcon className="w-5 h-5 text-[#534AB7] mx-auto" strokeWidth={1.5} />
              <p className="mt-2 text-sm text-[#5F5E5A]">No covers match this book title.</p>
              <Link
                to={`/cover/new?from=${fm.id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#534AB7] hover:underline"
                data-testid="book-create-cover-cta"
              >
                <Plus className="w-3.5 h-3.5" /> Create matching cover
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="book-covers">
              {covers.map((c) => (
                <Link
                  key={c.id}
                  to={`/cover/${c.id}`}
                  className="flex items-center gap-3 bg-white border border-[#D3D1C7] rounded-[10px] p-3 hover:border-[#534AB7] transition-colors"
                  data-testid={`book-cover-${c.id}`}
                >
                  <div className="w-14 h-20 rounded-md overflow-hidden bg-[#F1EFE8] flex-shrink-0">
                    {c.thumbnail_url ? (
                      <img src={c.thumbnail_url} alt={c.book_title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-[#5F5E5A]/50" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#2C2C2A] truncate">{c.book_title}</div>
                    <div className="text-[11px] text-[#5F5E5A]">{c.trim_size} · {c.page_count}pp</div>
                    {c.share_token && (
                      <div className="text-[11px] text-[#1D9E75] mt-0.5 inline-flex items-center gap-1">
                        <Share2 className="w-3 h-3" /> Public link
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}

          <h2 className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A] font-medium mt-8 mb-3">
            Linked listings ({listings.length})
          </h2>
          {listings.length === 0 ? (
            <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[10px] p-6 text-center">
              <FileText className="w-5 h-5 text-[#534AB7] mx-auto" strokeWidth={1.5} />
              <p className="mt-2 text-sm text-[#5F5E5A]">No Amazon listing generated yet.</p>
              <Link
                to="/dashboard"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#534AB7] hover:underline"
              >
                <Sparkles className="w-3.5 h-3.5" /> Generate listing copy
              </Link>
            </div>
          ) : (
            <div className="space-y-2" data-testid="book-listings">
              {listings.map((l) => (
                <div
                  key={l.id}
                  className="bg-white border border-[#D3D1C7] rounded-[10px] p-4"
                  data-testid={`book-listing-${l.id}`}
                >
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-[#534AB7] mt-0.5 shrink-0" strokeWidth={1.6} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2C2C2A] truncate">{l.book_title}</div>
                      <div className="text-[11px] text-[#5F5E5A] mt-0.5">
                        {(l.keywords || []).slice(0, 3).join(" · ") || "No keywords"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Sidebar: book metadata + quick actions */}
        <aside className="lg:sticky lg:top-6 self-start space-y-4">
          <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-5" data-testid="book-meta">
            <h2 className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A] font-medium mb-3">
              Book metadata
            </h2>
            <dl className="space-y-2 text-sm">
              <Row label="Title" value={fm.book_title} />
              {fm.subtitle && <Row label="Subtitle" value={fm.subtitle} />}
              <Row label="Author" value={fm.author || "—"} />
              {fm.publisher && <Row label="Publisher" value={fm.publisher} />}
              <Row label="Trim" value={fm.trim_size} />
              <Row label="ISBN" value={fm.isbn || "—"} mono />
              <Row label="Edition" value={fm.edition || "—"} />
              <Row label="Status" value={fm.status === "final" ? "Final" : "Draft"} />
            </dl>
            <div className="mt-4 pt-4 border-t border-[#D3D1C7] flex flex-col gap-2">
              <Link
                to={`/frontmatter/${fm.id}`}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] border border-[#D3D1C7] text-xs text-[#2C2C2A] hover:bg-[#F1EFE8]"
                data-testid="book-edit-fm-btn"
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit front matter
              </Link>
              {!covers.length && (
                <Link
                  to={`/cover/new?from=${fm.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] bg-[#534AB7] text-white text-xs font-medium hover:bg-[#3C3489]"
                  data-testid="book-new-cover-btn"
                >
                  <BookOpen className="w-3.5 h-3.5" /> Create matching cover
                </Link>
              )}
            </div>
          </div>

          <div className="bg-[#F1EFE8]/60 border border-[#D3D1C7] rounded-[10px] p-4 text-xs text-[#5F5E5A] leading-relaxed">
            <strong className="block text-[#2C2C2A] mb-1">How matching works</strong>
            We link covers and listings to this book by matching <em>book title</em> exactly
            (case-insensitive). Rename a project to attach or detach it.
          </div>
        </aside>
      </div>
    </Layout>
  );
};

const Layout = ({ children }) => (
  <div className="min-h-screen bg-[#FAFAF9]">
    <TopNav />
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      {children}
    </div>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="text-[11px] uppercase tracking-wider text-[#5F5E5A] shrink-0">{label}</dt>
    <dd className={`text-[#2C2C2A] text-right truncate ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
  </div>
);

export default BookOverview;
